import React, { useState, FormEvent, useRef, useEffect, JSX } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldCheck,
    Loader2,
    AlertCircle,
    ArrowRight,
    RefreshCcw,
    HardHat
} from "lucide-react";

export default function Verify(): JSX.Element {
    const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [timer, setTimer] = useState<number>(30);
    
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const nav = useNavigate();

    // Corporate Brand Colors
    const brandBlue = "#0054A6";
    const brandYellow = "#FFD100";

    useEffect(() => {
        const interval = setInterval(() => {
            if (timer > 0) setTimer(timer - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [timer]);

    const handleChange = (element: HTMLInputElement, index: number) => {
        if (isNaN(Number(element.value))) return false;
        const newOtp = [...otp];
        newOtp[index] = element.value.substring(element.value.length - 1);
        setOtp(newOtp);
        if (element.value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerify = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        const otpString = otp.join("");
        
        if (otpString.length < 6) {
            setError("Please enter the full 6-digit code.");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            await api.post("/auth/verify-otp", { otp: otpString });
            nav("/profile");
        } catch (err: any) {
            setError(err.response?.data?.message || "Invalid OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (timer > 0) return;
        setTimer(30);
        // Add api.post("/auth/resend-otp") logic here
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0a0f1d] relative overflow-hidden font-sans">
            {/* Structural Background Accents */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
                <div 
                    className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full blur-[120px]" 
                    style={{ backgroundColor: `${brandBlue}33` }} 
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-[460px] px-6"
            >
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-8 md:p-10 text-center">
                    <header className="mb-8 flex flex-col items-center">
                        <div className="bg-white p-4 rounded-xl mb-6 shadow-lg">
                            <img
                                src="/logo.jpeg"
                                alt="KCG Logo"
                                className="h-12 object-contain"
                            />
                        </div>
                        
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldCheck style={{ color: brandYellow }} size={20} />
                            <h1 className="text-xl font-black text-white uppercase tracking-wider">
                                Security Verification
                            </h1>
                        </div>
                        <p className="text-slate-400 text-sm">
                            Enter the 6-digit corporate code sent to your email.
                        </p>
                    </header>

                    <AnimatePresence mode="wait">
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                className="mb-6 p-4 rounded-lg bg-red-500/10 border-l-4 border-red-500 text-red-200 text-xs text-left flex items-center gap-3"
                            >
                                <AlertCircle size={16} className="shrink-0" />
                                <span>{error}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={handleVerify} className="space-y-8">
                        <div className="flex justify-between gap-2">
                            {otp.map((data, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength={1}
                                    ref={(el) => { inputRefs.current[index] = el; }}
                                    value={data}
                                    onChange={(e) => handleChange(e.target, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    style={{ caretColor: brandYellow }}
                                    className="w-12 h-14 bg-slate-900/50 border border-slate-700 text-white text-center text-xl font-bold rounded-xl focus:border-[#FFD100] focus:ring-1 focus:ring-[#FFD100] outline-none transition-all shadow-inner"
                                />
                            ))}
                        </div>

                        <div className="space-y-4">
                            <button
                                disabled={isLoading}
                                type="submit"
                                style={{ backgroundColor: brandBlue }}
                                className="w-full group text-white font-bold py-4 rounded-xl shadow-lg hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-3 border-b-4 border-black/20"
                            >
                                {isLoading ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : (
                                    <>
                                        VERIFY IDENTITY
                                        <HardHat size={18} style={{ color: brandYellow }} />
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={timer > 0}
                                className="flex items-center justify-center gap-2 w-full text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
                                style={{ color: timer === 0 ? brandYellow : "#94a3b8" }}
                            >
                                <RefreshCcw size={14} className={timer === 0 ? "animate-pulse" : ""} />
                                {timer > 0 ? `Resend code in ${timer}s` : "Request New Code"}
                            </button>
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
}